// The AI interview as a screen. The transcript is the office's record of
// what the client said; the assistant's questions, read-back and details
// wear the AI surface because it wrote them; the composer is fenced by
// the same limits the server enforces (twenty exchanges, twenty requests
// a minute) so a refusal never arrives as a surprise.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  answerInterview,
  createInterviewMatter,
  fetchInterview,
  IntakeApiError,
  INTERVIEW_MAX_TURNS,
  uploadInterviewDocument,
  type AnswerInterviewInput,
  type InterviewSession,
} from "../api/intake";
import { UPLOAD_ACCEPT, uploadProblem } from "../../../api/documents";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { caseTypeLabel } from "../../../lib/caseLabels";
import { matterRouteId, serverPracticeAreaLabel } from "../../../api/mock";
import { factLabels, labelOf, transcriptOf } from "./transcript";

const MAX_EXCHANGES = INTERVIEW_MAX_TURNS / 2;
// The limiter's window is a minute; after a 429 the composer waits it out
// instead of inviting the click that would be refused again.
const THROTTLE_MS = 60_000;

/** The details the model has filled so far - empties and non-scalars stay off the screen. */
function factRows(data: Record<string, unknown>): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string" && value.trim()) rows.push([labelOf(key), value.trim()]);
    else if (typeof value === "number") rows.push([labelOf(key), String(value)]);
  }
  return rows;
}

function nameOf(s: InterviewSession): string {
  if (s.clientName?.trim()) return s.clientName.trim();
  const first = s.extractedData.firstName;
  const last = s.extractedData.lastName;
  const parts = [first, last].filter((v): v is string => typeof v === "string" && v.trim() !== "");
  return parts.length > 0 ? parts.join(" ") : "Unnamed client";
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function statusOf(err: unknown): number | null {
  return err instanceof IntakeApiError ? err.status : null;
}

export function IntakeInterview() {
  const { sessionId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionKey = ["intake", "session", sessionId];

  const session = useQuery({
    queryKey: sessionKey,
    queryFn: () => fetchInterview(sessionId),
    retry: false,
    // The server deletes the session when the matter is created; a
    // cached copy would outlive it and show a live composer over a 404.
    gcTime: 0,
  });

  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [readyHint, setReadyHint] = useState(false);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [unread, setUnread] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const transcriptBox = useRef<HTMLDivElement>(null);

  // Every answer replaces the question list wholesale, so the pick is
  // the question's text, not its slot: a pick the new list no longer
  // holds falls back to the first question instead of pointing past it.
  const questions = session.data?.suggestedQuestions ?? [];
  const activeQ = Math.max(0, questions.indexOf(picked ?? ""));

  // The newest turn lands below the fold of a capped box; the box follows it.
  const turns = session.data?.history.length ?? 0;
  useEffect(() => {
    const el = transcriptBox.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  useEffect(() => {
    if (retryAt === null) return;
    const timer = setTimeout(() => setRetryAt(null), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [retryAt]);

  const fail = (err: unknown, fallback: string) => {
    const status = statusOf(err);
    setError(messageOf(err, fallback));
    setErrorStatus(status);
    if (status === 429) setRetryAt(Date.now() + THROTTLE_MS);
  };

  const clearError = () => {
    setError(null);
    setErrorStatus(null);
  };

  const answer = useMutation({
    mutationKey: ["intake", "answer", sessionId],
    mutationFn: (input: AnswerInterviewInput) => answerInterview(sessionId, input),
    onSuccess: async (result) => {
      setDraft("");
      clearError();
      setReadyHint(result.readyToCreate);
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
    onError: (err) => fail(err, "The answer was not taken."),
  });

  const upload = useMutation({
    mutationKey: ["intake", "upload", sessionId],
    mutationFn: (file: File) => uploadInterviewDocument(sessionId, file),
    onSuccess: async (result, file) => {
      clearError();
      // Only a read document leaves a turn in the transcript; an unread
      // one is still kept and staged, so the screen says so itself.
      if (result.read) await queryClient.invalidateQueries({ queryKey: sessionKey });
      else setUnread((cur) => [...cur, file.name]);
    },
    onError: (err) => fail(err, "The upload failed."),
  });

  const create = useMutation({
    mutationKey: ["intake", "create-case", sessionId],
    mutationFn: () => createInterviewMatter(sessionId),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["matters"] }),
        queryClient.invalidateQueries({ queryKey: ["clients"] }),
        queryClient.invalidateQueries({ queryKey: ["firm", "counts"] }),
        queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] }),
      ]);
      navigate(`/matter/${matterRouteId(result.matter)}`);
    },
    onError: (err) => fail(err, "The matter could not be created."),
  });

  if (session.isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading the interview">
            <Skeleton width="40%" height={20} />
            <Skeleton />
            <Skeleton width="80%" />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  if (!session.data) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-index-head">
            <span className="title">Interview</span>
          </div>
          <div role="alert" className="cc-form-error">
            {messageOf(session.error, "The interview could not be loaded.")}
          </div>
          <Link to="/intake" className="cc-link-btn">
            Back to intake
          </Link>
        </div>
      </main>
    );
  }

  const s = session.data;
  const entries = transcriptOf(s.history);
  const exchanges = Math.floor(s.history.length / 2);
  const capped = s.history.length >= INTERVIEW_MAX_TURNS;
  const throttled = retryAt !== null;
  const busy = answer.isPending || upload.isPending || create.isPending;
  const canSend = draft.trim().length > 0 && !busy && !capped && !throttled;
  const facts = factRows(s.extractedData);
  const activeQuestion = questions[activeQ];

  const send = () => {
    if (!canSend) return;
    answer.mutate({ questionId: activeQ, answer: draft.trim() });
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = uploadProblem(file);
    if (problem) {
      setError(problem);
      setErrorStatus(null);
      return;
    }
    upload.mutate(file);
  };

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">{nameOf(s)}</span>
          <span className="blurb">
            AI interview - {exchanges} of {MAX_EXCHANGES} exchanges used.
          </span>
        </div>

        <div className="intake-interview">
          <section className="intake-chat">
            <div ref={transcriptBox} className="intake-transcript" aria-label="Transcript">
              {entries.map((entry, i) =>
                entry.kind === "document" ? (
                  <div key={i} className="intake-doc-turn">
                    <span className="intake-chip">{entry.label}</span>
                    {entry.text && (
                      <span className="cc-form-hint">
                        <span className="ai-chip">AI</span> Read from it: {factLabels(entry.text)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div key={i} className="intake-turn">
                    {entry.question && (
                      <div className="intake-msg bot ai-panel">{entry.question}</div>
                    )}
                    <div className="intake-msg user">{entry.text}</div>
                  </div>
                ),
              )}
              {unread.map((name, i) => (
                <div key={i} className="intake-doc-turn">
                  <span className="intake-chip">{name}</span>
                  <span className="cc-form-hint">
                    Uploaded, but nothing could be read from it - it follows the matter, and staff
                    can re-read it from the document card there.
                  </span>
                </div>
              ))}
            </div>

            <div
              className="intake-questions"
              role="radiogroup"
              aria-label="Questions from the assistant"
            >
              {questions.length === 0 ? (
                <div className="cc-form-hint">
                  {capped
                    ? "The interview is at its limit."
                    : "The assistant has no more questions - add anything else below, or create the matter."}
                </div>
              ) : (
                questions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={i === activeQ}
                    className={"intake-tpl-row intake-question" + (i === activeQ ? " picked" : "")}
                    onClick={() => setPicked(q)}
                  >
                    <span className="ai-chip">AI</span>
                    <span>{q}</span>
                  </button>
                ))
              )}
            </div>

            <div className="intake-composer">
              <textarea
                className="cc-field intake-textarea"
                aria-label="Your answer"
                rows={3}
                maxLength={8000}
                disabled={capped || throttled}
                placeholder={
                  activeQuestion ? `Answering: ${activeQuestion}` : "Anything else the client said"
                }
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <div className="cc-form-actions">
                <button
                  type="button"
                  className="cc-link-btn"
                  disabled={busy || capped || throttled}
                  onClick={() => fileInput.current?.click()}
                >
                  {upload.isPending ? "Reading the document..." : "Upload a document"}
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept={UPLOAD_ACCEPT}
                  aria-label="Upload document"
                  style={{ display: "none" }}
                  onChange={onFile}
                />
                <span style={{ flex: 1 }} />
                <button type="button" className="cc-btn-primary" disabled={!canSend} onClick={send}>
                  {answer.isPending ? "Sending..." : "Send"}
                </button>
              </div>
              {capped && (
                <div className="cc-form-hint">
                  All {MAX_EXCHANGES} exchanges are used - create the matter with what was gathered.
                </div>
              )}
              {throttled && (
                <div className="cc-form-hint">The composer opens again in a minute.</div>
              )}
            </div>

            {error && (
              <div role="alert" className="cc-form-error">
                {error}
                {(errorStatus === 409 || errorStatus === 404) && (
                  <>
                    {" "}
                    <Link to="/intake">Back to intake</Link>
                  </>
                )}
              </div>
            )}
          </section>

          <aside className="intake-side">
            <div className="intake-ai-card ai-panel">
              <div className="aicard-head">
                <span className="aicard-title">What the assistant has so far</span>
                <span className="ai-chip">AI</span>
              </div>
              <div className="intake-done-row">
                <span className="intake-done-label">Practice area</span>
                <span>{serverPracticeAreaLabel(s.practiceArea)}</span>
              </div>
              {s.caseType && (
                <div className="intake-done-row">
                  <span className="intake-done-label">Case type</span>
                  <span>{caseTypeLabel(s.caseType)}</span>
                </div>
              )}
              {facts.map(([label, value]) => (
                <div key={label} className="intake-done-row">
                  <span className="intake-done-label">{label}</span>
                  <span>{value}</span>
                </div>
              ))}
              {facts.length === 0 && <div className="cc-form-hint">No client details yet.</div>}
            </div>

            {s.keyFacts.length > 0 && (
              <div className="intake-ai-card ai-panel">
                <div className="aicard-head">
                  <span className="aicard-title">Key facts</span>
                  <span className="ai-chip">AI</span>
                </div>
                <ul className="intake-facts">
                  {s.keyFacts.map((fact, i) => (
                    <li key={i}>{fact}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="cc-card intake-create">
              <div className="cc-card-title">Create the matter</div>
              <p className="intake-prose">
                {readyHint
                  ? "The assistant has enough to draft the matter."
                  : "Possible at any point. The matter lands in the review queue for an attorney to take."}
              </p>
              <button
                type="button"
                className="cc-btn-primary"
                disabled={busy}
                onClick={() => create.mutate()}
              >
                {create.isPending ? "Creating..." : "Create matter"}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
